import type { RouteRecordRaw } from "vue-router";
import FeatureFlags from "@/enum/FeatureFlags";
import { currentSettingsFallback, sevdeskImportTarget } from "@/views/settings/lib/redirects";

const Layout = () => import("@/layout/index.vue");

const MANAGER = ["manager"];
const MANAGER_OR_SPACE_ADMIN = ["manager", "space_admin"];
/** Integrations, contact import and junk mail; mirrors Roles::canManageIntegrations(). */
const INTEGRATIONS = ["manager", "tech_admin"];
const INTEGRATIONS_OR_SPACE_ADMIN = ["manager", "tech_admin", "space_admin"];

/**
 * The settings area: one sidebar entry, a settings shell (navigation card + content card) and
 * one child route per page. Every page carries its own `roles` / `cff`, so the permission store
 * drops what the current account may not see, and the settings navigation only shows entries
 * whose route exists.
 */
const settingsRoutes: RouteRecordRaw[] = [
  {
    path: "/settings",
    component: Layout,
    name: "Settings",
    meta: {
      requiresAuth: true,
      roles: INTEGRATIONS_OR_SPACE_ADMIN,
    },
    children: [
      {
        path: "",
        name: "SettingsShell",
        component: () => import("@/views/settings/SettingsLayout.vue"),
        // Only applies when no child matches: /settings for a team admin, whose account has no
        // "Allgemein" page.
        redirect: () => currentSettingsFallback(),
        meta: {
          title: "settings",
          icon: "sliders-h",
          // The sidebar shows this entry as a single item; the pages below are reached
          // through the settings navigation.
          sidebarLeaf: true,
          activeMenu: "/settings",
          // Keeps the shell mounted while moving between settings pages.
          shellKey: "settings",
        },
        children: [
          {
            path: "",
            name: "SettingsGeneral",
            component: () => import("@/views/settings/general/GeneralSettings.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER },
          },
          {
            path: "users",
            name: "SettingsUsers",
            component: () => import("@/views/settings/users/UsersList.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER },
          },
          {
            path: "users/:id",
            name: "SettingsUser",
            component: () => import("@/views/settings/users/UserDetail.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER },
          },
          {
            path: "spaces",
            name: "SettingsSpaces",
            component: () => import("@/views/settings/spaces/SpacesList.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER_OR_SPACE_ADMIN },
          },
          {
            path: "spaces/:id/:tab?",
            name: "SettingsSpace",
            component: () => import("@/views/settings/spaces/SpacePage.vue"),
            // Switching tabs changes the path; the page stays mounted per space.
            meta: { title: "settings", hidden: true, roles: MANAGER_OR_SPACE_ADMIN, pageKey: "id" },
          },
          {
            path: "time-tracking",
            name: "SettingsTimeTracking",
            component: () => import("@/views/settings/general/TimeTrackingSettings.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER, cff: [FeatureFlags.TASKS_APP] },
          },
          {
            path: "contacts/types",
            name: "SettingsContactTypes",
            component: () => import("@/views/settings/contacts/ContactTypes.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER, cff: [FeatureFlags.CONTACTS_APP] },
          },
          {
            path: "contacts/data-types",
            name: "SettingsContactDataTypes",
            component: () => import("@/views/settings/contacts/ContactDataTypes.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER, cff: [FeatureFlags.CONTACTS_APP] },
          },
          {
            path: "contacts/numbering",
            name: "SettingsContactNumbering",
            component: () => import("@/views/settings/contacts/ContactNumbering.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER, cff: [FeatureFlags.CONTACTS_APP] },
          },
          {
            path: "contacts/fields",
            name: "SettingsContactFields",
            component: () => import("@/views/settings/contacts/ContactFields.vue"),
            meta: { title: "settings", hidden: true, roles: MANAGER, cff: [FeatureFlags.CONTACTS_APP] },
          },
          {
            path: "email/mailboxes",
            name: "SettingsMailboxes",
            component: () => import("@/views/settings/email/Mailboxes.vue"),
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.EMAIL_PORTALS] },
          },
          {
            // Space admins see the mailboxes of their teams read-only; tech admins edit every one.
            path: "email/mailboxes/:id",
            name: "SettingsMailbox",
            component: () => import("@/views/settings/email/MailboxDetail.vue"),
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS_OR_SPACE_ADMIN, cff: [FeatureFlags.EMAIL_PORTALS] },
          },
          {
            path: "email/quarantine",
            name: "SettingsQuarantine",
            component: () => import("@/views/settings/email/Quarantine.vue"),
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS_OR_SPACE_ADMIN, cff: [FeatureFlags.EMAIL_PORTALS] },
          },
          {
            // Connected mailboxes (mail module): shared boxes plus a read-only view of the
            // users' personal boxes. The own personal boxes live in the profile.
            path: "email/connections",
            name: "SettingsMailConnections",
            component: () => import("@/views/settings/email/connections/Connections.vue"),
            props: { scope: "shared" },
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.MAIL_APP] },
          },
          {
            path: "email/connections/:id",
            name: "SettingsMailConnection",
            component: () => import("@/views/settings/email/connections/ConnectionDetail.vue"),
            props: { scope: "shared" },
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.MAIL_APP], pageKey: "id" },
          },
          {
            path: "integrations/webhooks",
            name: "SettingsWebhooks",
            component: () => import("@/views/settings/integrations/Webhooks.vue"),
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.WEBHOOKS] },
          },
          {
            path: "integrations/webhooks/new",
            name: "SettingsWebhookNew",
            component: () => import("@/views/manager/webhooks/WebhookEditor.vue"),
            meta: { title: "webhooks", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.WEBHOOKS] },
          },
          {
            path: "integrations/webhooks/:id",
            name: "SettingsWebhookEdit",
            component: () => import("@/views/manager/webhooks/WebhookEditor.vue"),
            meta: { title: "webhooks", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.WEBHOOKS] },
          },
          {
            path: "integrations/webhooks/:id/deliveries",
            name: "SettingsWebhookDeliveries",
            component: () => import("@/views/manager/webhooks/WebhookDeliveries.vue"),
            meta: { title: "webhooks", hidden: true, roles: INTEGRATIONS, cff: [FeatureFlags.WEBHOOKS] },
          },
          {
            path: "integrations/sevdesk",
            name: "SettingsSevDesk",
            component: () => import("@/views/settings/integrations/SevDesk.vue"),
            meta: {
              title: "settings",
              hidden: true,
              roles: INTEGRATIONS,
              cffAll: [FeatureFlags.SEVDESK_INTEGRATION, FeatureFlags.CONTACTS_APP],
            },
          },
          {
            // The import wizard page is gone (#307): the import is step 1 of the Abgleich tab now.
            path: "integrations/sevdesk/import/:runId?",
            name: "SettingsSevDeskImportWizard",
            redirect: (to) => sevdeskImportTarget(Array.isArray(to.params.runId) ? to.params.runId[0] : to.params.runId),
            meta: {
              title: "sevdesk",
              hidden: true,
              roles: INTEGRATIONS,
              cffAll: [FeatureFlags.SEVDESK_INTEGRATION, FeatureFlags.CONTACTS_APP],
            },
          },
          {
            // Messages of the background processes (tenant log); mirrors ListTenantLog's rule.
            path: "log",
            name: "SettingsLog",
            component: () => import("@/views/settings/system/TenantLog.vue"),
            meta: { title: "settings", hidden: true, roles: INTEGRATIONS },
          },
          {
            // Unknown settings URLs, and pages the account may not see (a team or tech admin on
            // a manager-only page), end here instead of on the 404 page.
            path: ":pathMatch(.*)*",
            name: "SettingsFallback",
            redirect: () => currentSettingsFallback(),
            meta: { hidden: true },
          },
        ],
      },
    ],
  },
];

export default settingsRoutes;
